'use client'

import { useState, useEffect, useCallback, useMemo } from 'react'
import { ChevronDown, ExternalLink, Megaphone, Radio, ShieldCheck, Upload, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { ProcessedTheme, ProcessedEntry, DomainDetail, BrowserRow, ProbeRun, EntryDetailData } from './DomainEntry/processedTypes'
import { watchClasses, compactDate, postTypeLabel, entryHostname, campaignCode, entryDisplayTitle } from './DomainEntry/processedTypes'
import { CopyChip, Highlight, CampField } from './DomainEntry/sharedComponents'
import { ProcessedEntryTimeline } from './DomainEntry/ProcessedEntryTimeline'
import { PipelineStepsWidget } from '../PipelineStepsWidget'
import { EntryScreenshotsPanel } from '../Entries/EntryScreenshotsPanel'
import { OriginalSubmissionWidget } from './OriginalSubmissionWidget'
import { DomainIntelligencePanel } from './DomainEntry/DomainIntelligencePanel'
import { BrowserDataPanel } from './DomainEntry/BrowserDataPanel'
import { ProbeRunsPanel } from './DomainEntry/ProbeRunsPanel'
import { parseDbTimestamp } from '@/lib/dates'

function VoteBadge({ label, alive, title }: { label: string; alive: boolean | null; title?: string }) {
  const cls = alive === true
    ? 'bg-green-50 border-green-200 text-green-700'
    : alive === false
    ? 'bg-secondary border-border text-muted-foreground'
    : 'bg-secondary border-border text-foreground'
  return (
    <span className={`inline-flex items-center gap-0.5 font-mono text-[8px] border px-1 py-0.5 ${cls}`} title={title}>
      {alive === true && <span className="h-1 w-1 rounded-full bg-green-500 animate-pulse shrink-0" />}
      {alive === false && <span className="h-1 w-1 rounded-full bg-subtle shrink-0" />}
      {label}
    </span>
  )
}

function EntryMetaPill({ children, theme, tone = 'neutral' }: { children: React.ReactNode; theme: ProcessedTheme; tone?: 'neutral' | 'red' | 'blue' }) {
  const dark = theme === 'dark'
  const cls = tone === 'red'
    ? dark ? 'border-destructive/40 bg-danger-surface text-destructive' : 'border-destructive/40 bg-danger-surface text-destructive'
    : tone === 'blue'
    ? dark ? 'border-signal/30 bg-signal-surface text-signal' : 'border-signal/30 bg-signal-surface text-signal'
    : dark ? 'border-border bg-muted text-foreground' : 'border-border bg-secondary text-muted-foreground'
  return <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${cls}`}>{children}</span>
}

function CreateCampaignPanel({
  entry,
  browserData,
  getToken,
}: {
  entry: ProcessedEntry
  browserData: BrowserRow[]
  getToken: () => Promise<string>
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [headline, setHeadline] = useState(() => entry.headline ?? entry.title ?? '')
  const [description, setDescription] = useState(
    () => entry.messageContent ?? entry.processedInputDescription ?? '',
  )
  const [imageMode, setImageMode] = useState<'screenshot' | 'upload'>('screenshot')
  const [selectedImage, setSelectedImage] = useState<{ base64: string; mime: string; fromR2Key: string | null } | null>(null)
  const [loadingR2Key, setLoadingR2Key] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null)

  const screenshotRows = useMemo(
    () => browserData.filter((r) => r.entryDomainId === entry.userEntryDomainId && r.r2Key),
    [browserData, entry.userEntryDomainId],
  )

  const switchMode = useCallback((mode: 'screenshot' | 'upload') => {
    setImageMode(mode)
    setSelectedImage(null)
  }, [])

  const handleSelectScreenshot = useCallback(async (r2Key: string) => {
    if (loadingR2Key) return
    setLoadingR2Key(r2Key)
    setSelectedImage(null)
    try {
      const token = await getToken()
      const res = await fetch('/api/screenshots/bykey', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ r2Key, turnstileToken: token }),
      })
      const json = await res.json()
      if (!json.success) { setResult({ ok: false, msg: 'Failed to load screenshot.' }); return }
      const scenarios = json.data?.data?.buffer?.scenarios
      const base64 = scenarios?.desktop?.screenshots?.[0]?.data ?? scenarios?.mobile?.screenshots?.[0]?.data
      if (!base64) { setResult({ ok: false, msg: 'No screenshot data in this capture.' }); return }
      setSelectedImage({ base64, mime: 'image/png', fromR2Key: r2Key })
      setResult(null)
    } catch {
      setResult({ ok: false, msg: 'Network error loading screenshot.' })
    } finally {
      setLoadingR2Key(null)
    }
  }, [getToken, loadingR2Key])

  const handleFileChange = useCallback((file: File) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string
      const [header, base64] = dataUrl.split(',')
      const mime = header.match(/data:([^;]+)/)?.[1] ?? 'image/png'
      setSelectedImage({ base64, mime, fromR2Key: null })
    }
    reader.readAsDataURL(file)
  }, [])

  const handleCreate = useCallback(async () => {
    if (!headline.trim() || saving) return
    setSaving(true)
    setResult(null)
    try {
      const body: Record<string, string> = {
        processedEntryId: entry.id,
        headline: headline.trim(),
        description: description.trim(),
      }
      if (selectedImage) {
        body.imageBase64 = selectedImage.base64
        body.imageMimeType = selectedImage.mime
        body.imageAltText = headline.trim()
      }
      const res = await fetch('/api/campaigns/ctt/promote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const payload = await res.json()
      if (payload.success) {
        setResult({ ok: true, msg: 'Brand campaign created successfully.' })
        setIsOpen(false)
      } else {
        setResult({ ok: false, msg: payload.error ?? 'Failed to create campaign.' })
      }
    } catch {
      setResult({ ok: false, msg: 'Network error.' })
    } finally {
      setSaving(false)
    }
  }, [headline, description, selectedImage, entry.id, saving])

  return (
    <div className="border border-signal/30 rounded overflow-hidden">
      <button
        onClick={() => setIsOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-3 py-2 bg-signal-surface hover:bg-signal-surface transition-colors text-left"
      >
        <Megaphone className="h-3 w-3 text-signal shrink-0" />
        <span className="font-mono text-[9px] uppercase tracking-widest text-signal">
          Create brand campaign
        </span>
        {result?.ok && (
          <span className="ml-2 font-mono text-[8px] uppercase tracking-widest text-success">✓ created</span>
        )}
        <ChevronDown className={`ml-auto h-3 w-3 text-signal transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="bg-card px-4 py-4 space-y-4">
          <div className="space-y-3">
            <CampField label="Headline">
              <input
                value={headline}
                onChange={(e) => setHeadline(e.target.value)}
                className="w-full h-8 border border-border rounded px-2.5 text-[13px] bg-card focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Public-facing headline…"
              />
            </CampField>
            <CampField label="Description">
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className="w-full border border-border rounded px-2.5 py-2 text-[13px] resize-y bg-card focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Public-facing description…"
              />
            </CampField>
          </div>

          <div>
            <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground mb-2">
              Campaign image <span className="text-foreground">(optional)</span>
            </div>

            <div className="flex gap-1 mb-3">
              {(['screenshot', 'upload'] as const).map((mode) => (
                <button
                  key={mode}
                  onClick={() => switchMode(mode)}
                  className={`font-mono text-[9px] uppercase tracking-widest border px-2.5 py-1 transition-colors ${
                    imageMode === mode
                      ? 'bg-primary text-primary-foreground border-signal/30 text-white'
                      : 'border-border text-muted-foreground hover:border-border hover:text-muted-foreground'
                  }`}
                >
                  {mode === 'screenshot' ? 'From screenshots' : 'Upload image'}
                </button>
              ))}
            </div>

            {imageMode === 'screenshot' && (
              <div>
                {screenshotRows.length === 0 ? (
                  <p className="font-mono text-[10px] text-muted-foreground">
                    No browser screenshots available for this domain.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {screenshotRows.map((r) => {
                      const isSelected = selectedImage?.fromR2Key === r.r2Key
                      const isLoading = loadingR2Key === r.r2Key
                      return (
                        <button
                          key={r.id}
                          onClick={() => handleSelectScreenshot(r.r2Key!)}
                          disabled={!!loadingR2Key}
                          className={`flex flex-col items-center gap-0.5 rounded border px-2.5 py-1.5 transition-colors disabled:opacity-50 ${
                            isSelected
                              ? 'border-signal bg-signal-surface text-signal'
                              : 'border-border text-muted-foreground hover:border-signal hover:bg-signal-surface'
                          }`}
                        >
                          <span className="font-mono text-[8px]">
                            {parseDbTimestamp(r.createdAt)?.toLocaleDateString('pt-PT') ?? '—'}
                          </span>
                          <span className="font-mono text-[8px]">
                            {isLoading ? '⟳ loading…' : isSelected ? '✓ selected' : 'select'}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )}

            {imageMode === 'upload' && (
              <label className="block cursor-pointer">
                <div
                  className="flex flex-col items-center gap-2 rounded border-2 border-dashed border-border px-4 py-5 text-center transition-colors hover:border-signal hover:bg-signal-surface"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault()
                    const file = e.dataTransfer.files[0]
                    if (file?.type.startsWith('image/')) handleFileChange(file)
                  }}
                >
                  <Upload className="h-5 w-5 text-muted-foreground" />
                  <p className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
                    {selectedImage && !selectedImage.fromR2Key
                      ? 'Image loaded ✓ · click to replace'
                      : 'Click or drag to upload'}
                  </p>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    if (f) handleFileChange(f)
                  }}
                />
              </label>
            )}

            {selectedImage && (
              <div className="mt-3 flex items-start gap-2">
                <img
                  src={`data:${selectedImage.mime};base64,${selectedImage.base64}`}
                  alt="Preview"
                  className="max-h-24 w-auto rounded border border-border bg-secondary object-contain"
                />
                <button
                  onClick={() => setSelectedImage(null)}
                  className="mt-0.5 text-muted-foreground hover:text-destructive transition-colors"
                  title="Remove image"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>

          {result && (
            <div className={`font-mono text-[11px] px-3 py-2 border ${
              result.ok
                ? 'bg-success-surface border-success/40 text-success'
                : 'bg-danger-surface border-destructive/40 text-destructive'
            }`}>
              {result.msg}
            </div>
          )}

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="font-mono text-[10px] uppercase tracking-widest"
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="ml-auto font-mono text-[10px] uppercase tracking-widest gap-1.5"
              disabled={saving || !headline.trim()}
              onClick={handleCreate}
            >
              <Megaphone className="h-3 w-3" />
              {saving ? 'Creating…' : 'Create campaign'}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export function ProcessedEntryCard({
  entry,
  expanded,
  detailMode = false,
  theme,
  query,
  onToggle,
  onOpenModal,
  onToggleInactive,
  getToken,
}: {
  entry: ProcessedEntry
  expanded: boolean
  detailMode?: boolean
  theme: ProcessedTheme
  query: string
  onToggle: () => void
  onOpenModal: (entry: ProcessedEntry, domainCreatedAt?: string) => void
  onToggleInactive: (userEntryDomainId: string, next: boolean) => void
  getToken: () => Promise<string>
}) {
  const c = watchClasses(theme)
  const [data, setData] = useState<EntryDetailData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!expanded || !entry.userEntryDomainId || data || loading) return
    setLoading(true)
    setError(null)
    fetch(`/api/campaigns/ctt/entry-domains?userEntryDomainId=${entry.userEntryDomainId}`)
      .then((r) => r.json())
      .then((res) => {
        if (res.success) setData(res.data)
        else setError(res.reason ?? 'Failed to load domain details')
      })
      .catch(() => setError('Network error loading domain details'))
      .finally(() => setLoading(false))
  }, [expanded, entry.userEntryDomainId, data, loading])

  const title = entryDisplayTitle(entry)
  const hostname = entryHostname(entry)
  const bAlive = entry.lastMobileIsAlive === true || entry.lastDesktopIsAlive === true
    ? true : (entry.lastMobileIsAlive === null && entry.lastDesktopIsAlive === null ? null : false)
  const curDomain = data?.domains.find((d) => d.id === entry.userEntryDomainId) ?? data?.domains[0] ?? null
  const curBrowserData = data?.browserData.filter((r) => r.entryDomainId === entry.userEntryDomainId) ?? []
  const latestBrowser = curBrowserData.reduce<BrowserRow | null>((latest, row) =>
    !latest || new Date(row.createdAt).getTime() > new Date(latest.createdAt).getTime() ? row : latest, null)
  const curProbeRuns = (data?.probeRuns.filter((r) => r.entryDomainId === entry.userEntryDomainId) ?? [])
    .sort((a, b) => {
      const nodeCompare = (a.probeNode ?? '').localeCompare(b.probeNode ?? '')
      if (nodeCompare !== 0) return nodeCompare
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })
  const domainMeta = curDomain?.domainMetadata ?? {}
  const domainInfo = curDomain?.domainInfo ?? {}
  const ip = domainMeta.ip_address ?? domainInfo.ip
  const asn = domainMeta.asn ?? domainInfo.asn
  const statusText = curDomain?.manualInactive ? 'Manual off' : domainMeta.is_active === true ? 'Active' : domainMeta.is_active === false ? 'Down' : 'Unknown'
  const evidenceSummary = [
    entry.isScam ? 'Reporter/classifier marked this entry as scam.' : 'This entry is not currently marked as scam.',
    curDomain ? `Domain status: ${statusText}.` : 'No enriched domain record is loaded.',
    ip ? `Resolved IP: ${ip}${asn ? ` (${asn})` : ''}.` : 'Resolved IP is not available.',
    `${curBrowserData.length} browser capture${curBrowserData.length === 1 ? '' : 's'} and ${curProbeRuns.length} probe run${curProbeRuns.length === 1 ? '' : 's'} are linked to this domain.`,
  ].join(' ')

  return (
    <article className={`${expanded && detailMode ? 'border ' + c.card : expanded ? '' : 'border-b'} ${c.line}`}>
      <div
        role={detailMode ? undefined : 'button'}
        tabIndex={detailMode ? undefined : 0}
        onClick={onToggle}
        onKeyDown={(event) => {
          if (detailMode) return
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onToggle()
          }
        }}
        className={`grid w-full grid-cols-[1fr_auto] gap-6 px-7 py-6 text-left transition-colors ${detailMode ? '' : 'cursor-pointer'} ${!detailMode && theme === 'dark' ? 'hover:bg-[#121a21]' : !detailMode ? 'hover:bg-secondary' : ''}`}
      >
        <div>
          <div className="flex flex-wrap items-center gap-2">
            {entry.userEntryId ? (
              <span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 ${c.line} ${c.soft}`}>
                <span className={`font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>User entry</span>
                <CopyChip value={entry.userEntryId} />
              </span>
            ) : (
              <span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 ${c.line} ${c.soft}`}>
                <span className={`font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>Entry</span>
                <CopyChip value={entry.id} />
              </span>
            )}
            {entry.isScam && <EntryMetaPill theme={theme} tone="red"><span className="h-1.5 w-1.5 rounded-full bg-destructive" />SCAM</EntryMetaPill>}
            {entry.userEntryDomainId && (
              <span className="inline-flex items-center gap-1.5">
                <VoteBadge label="B" alive={bAlive} title="Browser data" />
                <VoteBadge label="D" alive={entry.lastDomainIsActive ?? null} title="WHOIS domain metadata" />
                <VoteBadge label="P" alive={entry.lastProbeIsAlive ?? null} title="Probe run" />
              </span>
            )}
            <span className={`text-xs ${c.muted}`}>first seen {compactDate(entry.createdAt)}</span>
          </div>
          <h2 className={`mt-4 text-xl font-semibold leading-snug tracking-[-0.01em] ${c.text}`}>
            "<Highlight text={title} query={query} />"
          </h2>
          {entry.messageContent && (
            <p className={`mt-3 max-w-4xl text-[17px] leading-7 ${c.muted}`}>
              <Highlight text={entry.messageContent} query={query} />
            </p>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            {entry.postType && <EntryMetaPill theme={theme}><Radio className="h-3 w-3" />{postTypeLabel(entry.postType)}{entry.sender ? ` · ${entry.sender}` : ''}</EntryMetaPill>}
            {hostname && <EntryMetaPill theme={theme} tone="blue"><ExternalLink className="h-3 w-3" />{hostname}</EntryMetaPill>}
          </div>
        </div>
        <div className="hidden min-w-[190px] text-right md:block">
          <div className="font-mono text-[34px] leading-none tracking-tight">{String(entry.browserDataCount ?? 0).padStart(2, '0')}</div>
          <div className={`mt-2 font-mono text-[10px] uppercase tracking-[0.3em] ${c.label}`}>captures</div>
          <div className="mt-5 flex justify-end gap-1.5">
            <VoteBadge label="B" alive={bAlive} title="Browser data" />
            <VoteBadge label="D" alive={entry.lastDomainIsActive ?? null} title="WHOIS domain metadata" />
            <VoteBadge label="P" alive={entry.lastProbeIsAlive ?? null} title="Probe run" />
          </div>
        </div>
      </div>

      {expanded && (
        <div className={`border-t px-7 pb-7 pt-7 ${c.line}`}>
          {loading && <div className={`font-mono text-xs uppercase tracking-[0.28em] ${c.faint}`}>Loading entry intelligence...</div>}
          {error && <div className="font-mono text-xs text-destructive">{error}</div>}
          {!entry.userEntryDomainId && <div className={`font-mono text-xs uppercase tracking-[0.28em] ${c.faint}`}>No domain associated</div>}
          {data && (
            <>
              <div className="grid gap-5 grid-cols-1 lg:grid-cols-3">
                <ProcessedEntryTimeline
                  entry={entry}
                  domain={curDomain}
                  browserData={curBrowserData}
                  theme={theme}
                />
                <OriginalSubmissionWidget
                  r2Key={entry.rawEntryR2Key}
                  theme={theme}
                  getToken={getToken}
                  postType={entry.postType}
                  messageTypeId={entry.messageTypeId}
                  messageTypeText={entry.messageTypeName}
                />
                <EntryScreenshotsPanel rows={curBrowserData} theme={theme} getToken={getToken} />
              </div>
              <PipelineStepsWidget
                steps={entry.pipelineSteps ?? entry.pipeline_steps}
                theme={theme}
                className="mb-5"
              />
              <div className="grid gap-5 xl:grid-cols-[1.25fr_1fr_0.72fr]">
                <DomainIntelligencePanel entry={entry} domain={curDomain} theme={theme} />
                <BrowserDataPanel curBrowserData={curBrowserData} latestBrowser={latestBrowser} theme={theme} />
                <ProbeRunsPanel curProbeRuns={curProbeRuns} theme={theme} />
              </div>

              <section className={`mt-5 border p-6 border-destructive/40 bg-danger-surface`}>
                <div className="flex items-start gap-4">
                  <ShieldCheck className="mt-0.5 h-4 w-4 text-destructive" />
                  <div className="flex-1">
                    <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-destructive">// Evidence summary</div>
                    <p className={`mt-3 max-w-5xl text-[17px] leading-7 ${c.text}`}>
                      {evidenceSummary} <span className="font-mono text-destructive">{campaignCode(entry, curDomain)}</span>
                    </p>
                    <div className="mt-5 flex flex-wrap items-center gap-2">
                      {entry.userEntryDomainId && (entry.manualInactive ? (
                        <button onClick={() => onToggleInactive(entry.userEntryDomainId!, false)} className={`rounded-md border px-4 py-2 text-sm font-semibold ${c.line} ${c.soft}`}>Undo manual off</button>
                      ) : (
                        <button onClick={() => onToggleInactive(entry.userEntryDomainId!, true)} className="rounded-md bg-destructive px-4 py-2 text-sm font-semibold text-white hover:bg-destructive text-destructive-foreground">Request takedown</button>
                      ))}
                      <button onClick={() => onOpenModal(entry, curDomain?.domainMetadata?.create_date)} className={`rounded-md border px-4 py-2 text-sm font-semibold ${c.line} ${theme === 'dark' ? 'bg-card text-white' : 'bg-card text-foreground'}`}>Open campaign</button>
                      <div className="[&>div]:rounded-md"><CreateCampaignPanel entry={entry} browserData={data.browserData} getToken={getToken} /></div>
                    </div>
                  </div>
                </div>
              </section>
            </>
          )}
        </div>
      )}
    </article>
  )
}
